import {
  CheckCircle2Icon,
  CircleDashedIcon,
  CircleDotIcon,
  CirclePauseIcon,
  CircleSlashIcon,
  EyeIcon,
  type LucideIcon,
} from 'lucide-react';

import type { TaskAction, TaskStatus } from '../api';

export const STATUSES: readonly TaskStatus[] = [
  'pending',
  'inProgress',
  'awaitingReview',
  'completed',
  'deferred',
  'declined',
];

/** Badge variant, column dot color and icon of each status (labels: `tasks:status.<status>`). */
export const STATUS_META: Record<
  TaskStatus,
  {
    badge: 'secondary' | 'info' | 'warning' | 'success' | 'outline' | 'destructive';
    color: string;
    icon: LucideIcon;
  }
> = {
  pending: { badge: 'secondary', color: 'var(--subtle-foreground)', icon: CircleDashedIcon },
  inProgress: { badge: 'info', color: 'var(--info)', icon: CircleDotIcon },
  awaitingReview: { badge: 'warning', color: 'var(--warning)', icon: EyeIcon },
  completed: { badge: 'success', color: 'var(--success)', icon: CheckCircle2Icon },
  deferred: { badge: 'outline', color: 'var(--border-strong)', icon: CirclePauseIcon },
  declined: { badge: 'destructive', color: 'var(--destructive)', icon: CircleSlashIcon },
};

/** Workflow buttons in the order the card shows them; the first allowed one is the primary button. */
export const ACTION_ORDER: readonly TaskAction[] = [
  'start',
  'complete',
  'approve',
  'returnToWork',
  'resume',
  'renew',
  'pause',
  'defer',
  'decline',
];

/** Primary-looking actions (the others are secondary buttons or menu items). */
export const PRIMARY_ACTIONS: ReadonlySet<TaskAction> = new Set([
  'start',
  'complete',
  'approve',
  'resume',
  'renew',
]);

export const sortActions = (actions: readonly TaskAction[]): TaskAction[] =>
  [...actions].sort((a, b) => ACTION_ORDER.indexOf(a) - ACTION_ORDER.indexOf(b));

/**
 * The workflow action that moves a task from one Kanban column (status) to another, or null when no button does that.
 * Completing a task that requires review lands in "awaiting review" — the server decides.
 */
export function actionForMove(from: TaskStatus, to: TaskStatus): TaskAction | null {
  if (from === to) return null;
  switch (to) {
    case 'inProgress':
      return from === 'pending' ? 'start' : null;
    case 'pending':
      if (from === 'inProgress') return 'pause';
      if (from === 'deferred') return 'resume';
      if (from === 'awaitingReview') return 'returnToWork';
      if (from === 'completed' || from === 'declined') return 'renew';
      return null;
    case 'awaitingReview':
      return from === 'pending' || from === 'inProgress' ? 'complete' : null;
    case 'completed':
      if (from === 'awaitingReview') return 'approve';
      return from === 'pending' || from === 'inProgress' || from === 'deferred' ? 'complete' : null;
    case 'deferred':
      return from === 'pending' || from === 'inProgress' ? 'defer' : null;
    case 'declined':
      return from === 'pending' ? 'decline' : null;
    default:
      return null;
  }
}

/** Whether a card may be dropped into `to` by this user (the action exists and is one of the user's buttons). */
export function canMoveToStatus(
  task: { status: TaskStatus; allowedActions: readonly TaskAction[] },
  to: TaskStatus,
): boolean {
  if (task.status === to) return true;
  const action = actionForMove(task.status, to);
  return action !== null && task.allowedActions.includes(action);
}

export const isClosed = (status: TaskStatus) => status === 'completed' || status === 'declined';
